import React from 'react';
import { type Theme, useStyletron } from 'baseui';
import { useAtomValue } from 'jotai';
import type { StyleObject } from 'styletron-react';
import { TimeDisplayMode } from '../../../types';
import { getFrameToTime } from '../../../utils/frame-time';
import { numFramesAtom } from '../../atoms/annotation-atoms';
import { frameRateAtom } from '../../atoms/file-atoms';
import { timeDisplayModeAtom } from '../../atoms/tool-atoms';
import { RULER_TICK_KIND } from '../../constants/frame-timeline-v4';
import { classifyRulerTick, formatFrameLabel } from '../../utils/frame-timeline-v4';
const getCellStyle = (theme: Theme): StyleObject => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  paddingTop: theme.sizing.scale200,
  paddingBottom: theme.sizing.scale200
});
const getLabelStyle = (theme: Theme): StyleObject => ({
  height: `${theme.typography.LabelXSmall.lineHeight}`,
  color: theme.colors.contentTertiary,
  whiteSpace: 'nowrap',
  userSelect: 'none',
  ...theme.typography.LabelXSmall
});
const getMarkBandStyle = (theme: Theme): StyleObject => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  height: theme.sizing.scale300,
  marginTop: theme.sizing.scale100
});
const getTickStyle = (theme: Theme): StyleObject => ({
  width: theme.sizing.scale0,
  height: '100%',
  backgroundColor: theme.colors.contentTertiary
});
const DOT_SIZE = '5px';
const getDotStyle = (theme: Theme): StyleObject => ({
  width: DOT_SIZE,
  height: DOT_SIZE,
  borderRadius: '50%',
  backgroundColor: theme.colors.contentSecondary
});
function formatTickLabel(frame: number, isMajor: boolean, timeDisplayMode: TimeDisplayMode, frameRate: number | undefined, maxFrames: number): string {
  if (!isMajor) return '';
  if (timeDisplayMode === TimeDisplayMode.TIMESTAMP) {
    return getFrameToTime({
      frame,
      maxFrames,
      frameRate
    });
  }
  return formatFrameLabel(frame);
}
export const RulerTick = React.memo(function RulerTick({
  frame
}: {
  frame: number;
}) {
  const [css, theme] = useStyletron();
  const kind = classifyRulerTick(frame);
  const isMajor = kind === RULER_TICK_KIND.MAJOR;
  const timeDisplayMode = useAtomValue(timeDisplayModeAtom);
  const frameRate = useAtomValue(frameRateAtom);
  const maxFrames = useAtomValue(numFramesAtom);
  return <div className={css(getCellStyle(theme))}>
      <span className={css(getLabelStyle(theme))}>
        {formatTickLabel(frame, isMajor, timeDisplayMode, frameRate, maxFrames)}
      </span>
      {kind && <div className={css(getMarkBandStyle(theme))}>
          <span className={css(isMajor ? getTickStyle(theme) : getDotStyle(theme))} />
        </div>}
    </div>;
});